import { redirect } from "@tanstack/react-router";

import type { Project } from "@/openapi";

export const AuditFeature = {
  AGBW: "agbw",
  CONTINUOUS_AUDITOR: "continuous_auditor",
  DOCUMENT_ANALYSIS: "document_analysis",
  STATEMENT_EXTRACTION: "statement_extraction",
  TASKS_ERP: "tasks_erp",
  TASKS_AUDIT_FILE: "tasks_audit_file",
} as const;

export type AuditFeature = (typeof AuditFeature)[keyof typeof AuditFeature];

type FeatureGuardResult = { allowed: true } | { allowed: false };
type ClientWithFeatures = Pick<Project, "features">;

export function hasTasksFeature(client: ClientWithFeatures): boolean {
  return (
    client.features.includes(AuditFeature.TASKS_AUDIT_FILE) ||
    client.features.includes(AuditFeature.TASKS_ERP)
  );
}

export function hasErpTasksFeature(client: ClientWithFeatures): boolean {
  return client.features.includes(AuditFeature.TASKS_ERP);
}

export function hasFilesFeature(client: ClientWithFeatures): boolean {
  return client.features.includes(AuditFeature.TASKS_AUDIT_FILE);
}

export function hasDocumentAnalysisFeature(
  client: ClientWithFeatures,
): boolean {
  return client.features.includes(AuditFeature.DOCUMENT_ANALYSIS);
}

export function hasStatementExtractionFeature(
  client: ClientWithFeatures,
): boolean {
  return client.features.includes(AuditFeature.STATEMENT_EXTRACTION);
}

export function hasAgbwFeature(client: ClientWithFeatures): boolean {
  return client.features.includes(AuditFeature.AGBW);
}

/** Return whether the client has access to Continuous Auditor v2 results. */
export function hasContinuousAuditorFeature(
  client: ClientWithFeatures,
): boolean {
  return client.features.includes(AuditFeature.CONTINUOUS_AUDITOR);
}

export function guardTasksFeature(client: Project): FeatureGuardResult {
  if (!hasTasksFeature(client)) {
    return { allowed: false };
  }
  return { allowed: true };
}

export function guardDocumentAnalysisFeature(
  client: Project,
): FeatureGuardResult {
  if (!hasDocumentAnalysisFeature(client)) {
    return { allowed: false };
  }
  return { allowed: true };
}

export function guardStatementExtractionFeature(
  client: Project,
): FeatureGuardResult {
  if (!hasStatementExtractionFeature(client)) {
    return { allowed: false };
  }
  return { allowed: true };
}

export function guardAgbwFeature(client: Project): FeatureGuardResult {
  if (!hasAgbwFeature(client)) {
    return { allowed: false };
  }
  return { allowed: true };
}

/** Resolve route access for the independently configurable v2 module. */
export function guardContinuousAuditorFeature(
  client: Project,
): FeatureGuardResult {
  if (!hasContinuousAuditorFeature(client)) {
    return { allowed: false };
  }
  return { allowed: true };
}

export function redirectToClientOverview(clientId: string): never {
  throw redirect({
    to: "/auditor/clients/$id",
    params: { id: clientId },
  });
}
